import { toast as sonnerToast } from "sonner";
import { cn } from "~/lib/utils";
import { Icon, type IconType } from "../icons/icon";

type ToastType = "success" | "error" | "info" | "warning" | "loading";

interface ToastProps {
  id: string | number;
  type: ToastType;
  message: string;
}

const toastIcons: Record<ToastType, { icon: IconType; className: string }> = {
  success: { icon: "check-circle", className: "text-primary" },
  info: { icon: "info", className: "text-muted-foreground" },
  warning: { icon: "warning", className: "text-warning" },
  error: { icon: "error", className: "text-destructive" },
  loading: { icon: "loader", className: "text-muted-foreground animate-spin" },
};

export function Toast({ id, type, message }: ToastProps) {
  const { icon, className: iconClassName } = toastIcons[type];
  const isUrgent = type === "error" || type === "warning";

  return (
    <div
      role={isUrgent ? "alert" : "status"}
      aria-live={isUrgent ? "assertive" : "polite"}
      aria-atomic="true"
      className={cn(
        "flex items-center gap-3 w-full md:max-w-[364px] p-3 rounded-lg shadow-lg pointer-events-auto",
        "bg-card text-foreground border border-border",
      )}
    >
      <Icon i={icon} className={cn("size-4 shrink-0", iconClassName)} aria-hidden="true" />
      <p className="flex-1 text-sm">{message}</p>
      {type !== "loading" && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            sonnerToast.dismiss(id);
          }}
          aria-label="Close notification"
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon i="close" className="size-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export type { ToastType };
